import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useUrlSearch } from "@/lib/useUrlSearch";
import { useToast } from "@/context/ToastContext";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ClipboardList, PackagePlus, Plus, ScanLine, Search, SlidersHorizontal } from "lucide-react";
import type { StockSummaryRow, StockUnitRecord, WarehouseRecord } from "@/types/inventory";
import { StockTab } from "@/components/inventory/StockTab";
import { RollsTab } from "@/components/inventory/RollsTab";
import { LotsTab } from "@/components/inventory/LotsTab";
import { LedgerTab } from "@/components/inventory/LedgerTab";
import { ReceiveDialog } from "@/components/inventory/ReceiveDialog";
import { AdjustDialog } from "@/components/inventory/AdjustDialog";
import { TransferDialog } from "@/components/inventory/TransferDialog";
import { QcDialog } from "@/components/inventory/QcDialog";
import { UnitHistoryDialog } from "@/components/inventory/UnitHistoryDialog";

const TABS = [
  { key: "stock", label: "Stock" },
  { key: "rolls", label: "Rolls" },
  { key: "lots", label: "Lots" },
  { key: "ledger", label: "Ledger" },
] as const;
type Tab = (typeof TABS)[number]["key"];

export function InventoryPage() {
  const { hasPermission } = useAuth();
  const { error } = useToast();
  const [scan, setScan] = useState("");
  const [inspect, setInspect] = useState<StockUnitRecord | null>(null);
  const canReceive = hasPermission("inventory.receive");
  const canAdjust = hasPermission("inventory.adjust");
  const [urlParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(TABS.some((t) => t.key === urlParams.get("tab")) ? (urlParams.get("tab") as Tab) : "stock");
  const [warehouses, setWarehouses] = useState<WarehouseRecord[]>([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [search, setSearch] = useUrlSearch();
  const [product, setProduct] = useState({ id: "", variant: "" });
  const [reloadKey, setReloadKey] = useState(0);

  const [receive, setReceive] = useState<"PURCHASE" | "OPENING" | null>(null);
  const [adjust, setAdjust] = useState<StockUnitRecord[] | null>(null);
  const [transfer, setTransfer] = useState<StockUnitRecord[] | null>(null);
  const [history, setHistory] = useState<string | null>(null);

  useEffect(() => {
    api.get("/api/Warehouse/List").then((r) => setWarehouses(unwrapArray<WarehouseRecord>(r))).catch(() => setWarehouses([]));
  }, []);

  const refresh = () => setReloadKey((k) => k + 1);
  /** A scanner types the code and presses Enter: open that roll */
  const doScan = async () => {
    const code = scan.trim();
    if (!code) return;
    try {
      const u = unwrapObject<StockUnitRecord>(await api.get(`/api/Inventory/Scan/${encodeURIComponent(code)}`));
      setHistory(u.id);
      setScan("");
    } catch (err: any) {
      error("Not found", err.message || `No roll has the code ${code}`);
    }
  };
  const openRolls = (row: StockSummaryRow) => {
    setProduct({ id: row.product_id, variant: row.variant_id || "" });
    setTab("rolls");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Inventory</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Rolls, bales and bags in your warehouses, by dye lot, with every movement recorded</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-44">
            <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none z-10" />
            <Input placeholder="Scan roll code" value={scan} onChange={(e) => setScan(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doScan()} className="pl-10 h-9 text-sm" aria-label="Scan a roll code" />
          </div>
          <Button asChild variant="outline" size="sm" className="h-9 gap-1.5">
            <Link to="/inventory/receipts">
              <ClipboardList className="size-4" />
              Receipts
            </Link>
          </Button>
          {canAdjust && (
            <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => setAdjust([])}>
              <SlidersHorizontal className="size-4" />
              Add found stock
            </Button>
          )}
          {canReceive && (
            <>
              <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => setReceive("OPENING")}>
                <Plus className="size-4" />
                Opening stock
              </Button>
              <Button size="sm" className="h-9 gap-1.5 shadow-sm" onClick={() => setReceive("PURCHASE")}>
                <PackagePlus className="size-4" />
                Receive goods
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-3 lg:items-center justify-between">
        <div className="flex gap-1 rounded-lg bg-muted p-1 w-fit">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`h-8 px-4 rounded-md text-sm font-medium transition-colors ${tab === t.key ? "bg-background shadow-xs text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none z-10" />
            <Input placeholder="Search product, roll or lot…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 h-9 text-sm" />
          </div>
          <select
            className="h-9 rounded-lg border border-input bg-background px-3 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring"
            value={warehouseId}
            onChange={(e) => setWarehouseId(e.target.value)}
            aria-label="Warehouse"
          >
            <option value="">All warehouses</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {tab === "stock" && <StockTab warehouseId={warehouseId} search={search} reloadKey={reloadKey} onOpenRolls={openRolls} />}
      {tab === "rolls" && (
        <RollsTab
          warehouseId={warehouseId}
          search={search}
          productId={product.id}
          variantId={product.variant}
          reloadKey={reloadKey}
          onClearProduct={() => setProduct({ id: "", variant: "" })}
          onHistory={setHistory}
          onTransfer={setTransfer}
          onAdjust={setAdjust}
          onInspect={setInspect}
        />
      )}
      {tab === "lots" && <LotsTab search={search} reloadKey={reloadKey} />}
      {tab === "ledger" && <LedgerTab warehouseId={warehouseId} search={search} reloadKey={reloadKey} />}

      <ReceiveDialog open={!!receive} mode={receive || "PURCHASE"} onClose={() => setReceive(null)} onSaved={refresh} />
      <AdjustDialog open={!!adjust} units={adjust || []} onClose={() => setAdjust(null)} onSaved={refresh} />
      <QcDialog unit={inspect} onClose={() => setInspect(null)} onSaved={refresh} />
      <TransferDialog open={!!transfer} units={transfer || []} onClose={() => setTransfer(null)} onSaved={refresh} />
      <UnitHistoryDialog unitId={history} onClose={() => setHistory(null)} />
    </div>
  );
}
